import useSwr from "swr"
import { DashboardApi } from "../../../apis"
import HttpHandler from "../../../utils/http-handler"
import { message } from "../../../utils/message"
import {
  DateRange,
  LowMarginOrders,
  MarginByBrand,
  MarginByCustomer,
  MarginByEmployee,
  MarginByProduct,
  MarginSummary,
  MarginTrend,
  ReturnsByCustomer,
  ReturnsByEmployee,
  ReturnsByProduct,
  ReturnsSummary,
  ReturnsTrend,
  SalesAnalyticsByCategory,
  SalesAnalyticsIncompleteOrders,
  SalesAnalyticsSummary,
  SalesAnalyticsTopCustomers,
  SalesAnalyticsTopProducts,
  SalesAnalyticsTrend
} from "./types"

export function useSalesAnalyticsSummary(range: DateRange | null, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<SalesAnalyticsSummary | undefined>(
    hasAccess && range ? `ord.sales-analytics.summary.${range.year}-${range.month}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsSummary({ year: range.year, month: range.month, emp: employeeId })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

export function useSalesAnalyticsTrend(range: DateRange | null, compareYoY: boolean, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<SalesAnalyticsTrend | undefined>(
    hasAccess && range ? `ord.sales-analytics.trend.${range.year}-${range.month}.${compareYoY ? "yoy" : "no"}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsTrend({
          year       : range.year,
          month      : range.month,
          emp        : employeeId,
          granularity: "month",
          ...(compareYoY ? { yoy: 1 as const } : {})
        })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

export function useSalesAnalyticsTopCustomers(range: DateRange | null, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<SalesAnalyticsTopCustomers | undefined>(
    hasAccess && range ? `ord.sales-analytics.top-customers.${range.year}-${range.month}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsTopCustomers({ year: range.year, month: range.month, emp: employeeId })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

export function useSalesAnalyticsTopProducts(range: DateRange | null, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<SalesAnalyticsTopProducts | undefined>(
    hasAccess && range ? `ord.sales-analytics.top-products.${range.year}-${range.month}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsTopProducts({ year: range.year, month: range.month, emp: employeeId })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

export function useSalesAnalyticsByCategory(range: DateRange | null, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<SalesAnalyticsByCategory | undefined>(
    hasAccess && range ? `ord.sales-analytics.by-category.${range.year}-${range.month}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsByCategory({ year: range.year, month: range.month, emp: employeeId })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

export function useSalesAnalyticsIncompleteOrders(range: DateRange | null, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<SalesAnalyticsIncompleteOrders | undefined>(
    hasAccess && range ? `ord.sales-analytics.incomplete-orders.${range.year}-${range.month}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsIncompleteOrders({ year: range.year, month: range.month, emp: employeeId })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

function makeHook<T>(
  key: string,
  fetcher: (range: DateRange, employeeId?: string | null) => Promise<T>
) {
  return function useHook(range: DateRange | null, hasAccess: boolean, employeeId?: string | null) {
    return useSwr<T | undefined>(
      hasAccess && range ? `ord.sales-analytics.${key}.${range.year}-${range.month}.${employeeId ?? "all"}` : null,
      async () => {
        if (!range) return undefined
        try { return await fetcher(range, employeeId) } catch (err) {
          message.error((err as HttpHandler)?.message!)
          return undefined
        }
      },
      { revalidateOnFocus: false, keepPreviousData: true }
    )
  }
}

export const useMarginSummary = makeHook<MarginSummary>("margin-summary", (r, employeeId) => DashboardApi.salesAnalyticsMarginSummary({ year: r.year, month: r.month, emp: employeeId }))

export function useMarginTrend(range: DateRange | null, compareYoY: boolean, hasAccess: boolean, employeeId?: string | null) {
  return useSwr<MarginTrend | undefined>(
    hasAccess && range ? `ord.sales-analytics.margin-trend.${range.year}-${range.month}.${compareYoY ? "yoy" : "no"}.${employeeId ?? "all"}` : null,
    async () => {
      if (!range) return undefined
      try {
        return await DashboardApi.salesAnalyticsMarginTrend({
          year : range.year,
          month: range.month,
          emp  : employeeId,
          ...(compareYoY ? { yoy: 1 as const } : {})
        })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
        return undefined
      }
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  )
}

export const useMarginByCustomer = makeHook<MarginByCustomer>("margin-by-customer", (r, employeeId) => DashboardApi.salesAnalyticsMarginByCustomer({ year: r.year, month: r.month, emp: employeeId }))

export const useMarginByProduct = makeHook<MarginByProduct>("margin-by-product", (r, employeeId) => DashboardApi.salesAnalyticsMarginByProduct({ year: r.year, month: r.month, emp: employeeId }))

export const useMarginByBrand = makeHook<MarginByBrand>("margin-by-brand", (r, employeeId) => DashboardApi.salesAnalyticsMarginByBrand({ year: r.year, month: r.month, emp: employeeId }))

export const useMarginByEmployee = makeHook<MarginByEmployee>("margin-by-employee", (r, employeeId) => DashboardApi.salesAnalyticsMarginByEmployee({ year: r.year, month: r.month, emp: employeeId }))

export const useLowMarginOrders = makeHook<LowMarginOrders>("low-margin-orders", (r, employeeId) => DashboardApi.salesAnalyticsLowMarginOrders({ year: r.year, month: r.month, emp: employeeId }))

export const useReturnsSummary = makeHook<ReturnsSummary>("returns-summary", (r, employeeId) => DashboardApi.salesAnalyticsReturnsSummary({ year: r.year, month: r.month, emp: employeeId }))
export const useReturnsTrend = makeHook<ReturnsTrend>("returns-trend", (r, employeeId) => DashboardApi.salesAnalyticsReturnsTrend({ year: r.year, month: r.month, emp: employeeId }))
export const useReturnsByProduct = makeHook<ReturnsByProduct>("returns-by-product", (r, employeeId) => DashboardApi.salesAnalyticsReturnsByProduct({ year: r.year, month: r.month, emp: employeeId }))
export const useReturnsByCustomer = makeHook<ReturnsByCustomer>("returns-by-customer", (r, employeeId) => DashboardApi.salesAnalyticsReturnsByCustomer({ year: r.year, month: r.month, emp: employeeId }))
export const useReturnsByEmployee = makeHook<ReturnsByEmployee>("returns-by-employee", (r, employeeId) => DashboardApi.salesAnalyticsReturnsByEmployee({ year: r.year, month: r.month, emp: employeeId }))
